import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { Alert, Button, Divider, Input } from "antd";
import { changePassword, getProfile, updateAuth } from "../../store";
import { validatePassword } from "../utility/scripts/validate_password.js";
import { remove } from "../../store/localstorage";
import { ACCOUNT_PAGE, LOGIN_PAGE } from "../../configuration/paths";

// Full-screen forced password change. The route gate (configuration/Routes.jsx) sends a
// user here whenever profile.must_change_password is true (an admin assigned their
// password), and blocks every other authenticated route until they pick their own.
// On success the backend clears the flag; we refresh the profile so the gate opens.
export const ForcedChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState({ oldPass: "", newPass: "", reapeatPass: "" });
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = () => {
    let submitErrors = validatePassword(passwords.newPass);
    if (passwords.oldPass.length === 0) {
      submitErrors = submitErrors.concat(["Current password is missing"]);
    }
    if (passwords.newPass.length === 0) {
      submitErrors = submitErrors.concat(["New password is missing"]);
    } else if (passwords.newPass !== passwords.reapeatPass) {
      submitErrors = submitErrors.concat(["Your new passwords do not match"]);
    }
    if (submitErrors.length > 0) {
      setErrors(submitErrors);
      return;
    }
    setErrors([]);
    setSubmitting(true);
    dispatch(changePassword(passwords)).then(async (response) => {
      const errs = response.payload?.errors;
      if (errs?.length) {
        setErrors(errs);
        setSubmitting(false);
      } else {
        // Flag is cleared server-side; refresh profile so the gate stops redirecting here.
        await dispatch(getProfile());
        navigate(ACCOUNT_PAGE);
      }
    });
  };

  const logout = () => {
    dispatch(updateAuth({ token: null }));
    remove("auth_token");
    navigate(LOGIN_PAGE);
  };

  return (
    <div style={{ maxWidth: 420, margin: "48px auto", padding: "0 16px" }}>
      <h2>Set a new password</h2>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Your password was set by an administrator. Please choose your own password to continue."
      />
      <b>Current password</b>
      <Input.Password
        value={passwords.oldPass}
        onChange={(e) => setPasswords({ ...passwords, oldPass: e.target.value })}
      />
      <b>New password</b>
      <Input.Password
        value={passwords.newPass}
        onChange={(e) => setPasswords({ ...passwords, newPass: e.target.value })}
      />
      <p style={{ marginTop: 8 }}>Your new password needs:</p>
      <ul>
        <li>A minimum of 8 characters</li>
        <li>At least 1 lowercase and 1 uppercase letter</li>
        <li>At least 1 number</li>
        <li>At least 1 special character ({`! # $ % & ( ) * + , - . < = > ? @ ^ _ | ~`})</li>
      </ul>
      <b>Repeat new password</b>
      <Input.Password
        value={passwords.reapeatPass}
        onChange={(e) => setPasswords({ ...passwords, reapeatPass: e.target.value })}
      />
      {errors.length > 0 && (
        <div>
          <Divider />
          <ul>
            {errors.map((e) => (
              <li key={e} style={{ color: "#cf1322" }}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
        <Button onClick={logout}>Log out</Button>
        <Button type="primary" loading={submitting} onClick={submit}>Set password</Button>
      </div>
    </div>
  );
};

export default ForcedChangePassword;
